import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { jobTrackerAPI, oauthAPI, freeToolsAPI } from '../services/api';
import { CreditCounter, PaymentModal } from './CreditComponents';
import AddJobModal from './AddJobModal';
import EditJobModal from './EditJobModal';
import logo from '../assets/logo.png';

const JobTracker = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [limits, setLimits] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedAppForEdit, setSelectedAppForEdit] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFitScoreModal, setShowFitScoreModal] = useState(false);
  const [selectedAppForFitScore, setSelectedAppForFitScore] = useState(null);
  const [fitScoreLoading, setFitScoreLoading] = useState(false);
  const [oauthStatus, setOauthStatus] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showReminderPopover, setShowReminderPopover] = useState(null);
  const [reminderDate, setReminderDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if user is Pro
  const isPro = limits?.plan === 'pro' || user?.subscription_active || user?.plan === 'pro_monthly' || user?.plan === 'founding_member';

  // Check for payment return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setSuccessMessage('🎉 Payment successful! Your credits have been added.');
      // Clear the URL params after showing message
      setTimeout(() => {
        setSearchParams({});
        setSuccessMessage(null);
      }, 5000);
    } else if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled. No charges were made.');
      setTimeout(() => {
        setSearchParams({});
        setError(null);
      }, 5000);
    }
  }, [searchParams, setSearchParams]);

  // Active statuses that count toward the 20-limit
  const activeStatuses = ['Applied', 'Interview', 'Offer', 'On Hold'];

  // Job Application Statuses
  const statuses = [
    { value: 'all', label: 'All Applications', color: 'gray' },
    { value: 'Draft', label: 'Draft', color: 'gray' },
    { value: 'Applied', label: 'Applied', color: 'blue' },
    { value: 'Interview', label: 'Interview', color: 'yellow' },
    { value: 'Offer', label: 'Offer', color: 'green' },
    { value: 'On Hold', label: 'On Hold', color: 'orange' },
    { value: 'Rejected', label: 'Rejected', color: 'red' },
    { value: 'Withdrawn', label: 'Withdrawn', color: 'gray' },
    { value: 'Archived', label: 'Archived', color: 'gray' }
  ];

  useEffect(() => {
    loadData();
  }, [selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [limitsData, statsData, applicationsData, oauthData] = await Promise.all([
        jobTrackerAPI.getLimits(),
        jobTrackerAPI.getStats(),
        jobTrackerAPI.getApplications(selectedStatus === 'all' ? null : selectedStatus),
        oauthAPI.getStatus()
      ]);

      setLimits(limitsData);
      setStats(statsData);
      setApplications(applicationsData);
      setOauthStatus(oauthData);

    } catch (err) {
      console.error('Failed to load job tracker data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApplication = async (formData) => {
    try {
      setError(null);
      
      // Validate required fields
      if (!formData.company.trim() || !formData.role.trim()) {
        setError('Company and role are required.');
        return false;
      }

      // Check if we're at the active limit
      if (limits && !limits.can_add_new && activeStatuses.includes(formData.status)) {
        setShowLimitModal(true);
        return false;
      }

      const applicationData = {
        ...formData,
        applied_date: formData.applied_date ? new Date(formData.applied_date).toISOString() : null,
        interview_date: formData.interview_date ? new Date(formData.interview_date).toISOString() : null,
        follow_up_date: formData.follow_up_date ? new Date(formData.follow_up_date).toISOString() : null
      };

      await jobTrackerAPI.createApplication(applicationData);
      
      setShowCreateForm(false);
      await loadData();
      return true;

    } catch (err) {
      console.error('Failed to create application:', err);
      
      if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (errorData.error === 'lite_active_limit_exceeded') {
          setShowLimitModal(true);
        } else if (errorData.error === 'lite_new_limit_exceeded') {
          setError(errorData.message);
        } else {
          setError('Failed to create application. Please check your input.');
        }
      } else {
        setError('Failed to create application. Please try again.');
      }
      return false;
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      // Check if moving to an active status would exceed limit
      if (limits && !limits.can_add_new && activeStatuses.includes(newStatus)) {
        const currentApp = applications.find(a => a.id === applicationId);
        if (currentApp && !activeStatuses.includes(currentApp.status)) {
          setShowLimitModal(true);
          return;
        }
      }
      
      await jobTrackerAPI.updateApplication(applicationId, { status: newStatus });
      await loadData();
    } catch (err) {
      console.error('Failed to update application status:', err);
      setError('Failed to update application status.');
    }
  };

  const handleArchiveApplication = async (applicationId) => {
    try {
      await jobTrackerAPI.updateApplication(applicationId, { status: 'Archived' });
      await loadData();
    } catch (err) {
      console.error('Failed to archive application:', err);
      setError('Failed to archive application.');
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await jobTrackerAPI.deleteApplication(applicationId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete application:', err);
      setError('Failed to delete application.');
    }
  };

  // Handle opening edit modal
  const handleEditApplication = (application) => {
    setSelectedAppForEdit(application);
    setShowEditForm(true);
  };

  // Handle saving edited application
  const handleSaveEditedApplication = async (applicationId, updateData) => {
    try {
      setError(null);
      await jobTrackerAPI.updateApplication(applicationId, updateData);
      setShowEditForm(false);
      setSelectedAppForEdit(null);
      await loadData();
      return true;
    } catch (err) {
      console.error('Failed to update application:', err);
      setError('Failed to update application. Please try again.');
      return false;
    }
  };

  const handleOpenFitScore = (application) => {
    // Check if job description exists (min 200 chars for accurate matching)
    if (!application.job_description || application.job_description.trim().length < 200) {
      setError('Please add a full job description first (at least 200 characters). Click the Edit button to add one.');
      return;
    }
    setSelectedAppForFitScore(application);
    setShowFitScoreModal(true);
  };

  // Old handleRunFitScore removed - now using FitScoreModal with handleCalculate

  const getStatusColor = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? statusObj.color : 'gray';
  };

  const getFitScoreColor = (score) => {
    if (score === null || score === undefined) return { bg: 'bg-gray-100', text: 'text-gray-400', label: '—' };
    if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-700', label: `${score}%` };
    if (score >= 50) return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: `${score}%` };
    return { bg: 'bg-red-100', text: 'text-red-700', label: `${score}%` };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Pro Feature: Export CSV
  const handleExportCSV = async () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      setExportLoading(true);
      const blob = await jobTrackerAPI.exportCSV();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'job_applications.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage('CSV exported successfully!');
    } catch (err) {
      if (err.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        setError('Failed to export CSV. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Pro Feature: Set Reminder
  const handleSetReminder = async (appId) => {
    if (!isPro) {
      setShowUpgradeModal(true);
      setShowReminderPopover(null);
      return;
    }
    
    if (!reminderDate) {
      setError('Please select a reminder date.');
      return;
    }
    
    try {
      await jobTrackerAPI.createReminder(appId, new Date(reminderDate).toISOString());
      setSuccessMessage('Reminder set successfully!');
      setShowReminderPopover(null);
      setReminderDate('');
      await loadData();
    } catch (err) {
      if (err.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        setError('Failed to set reminder. Please try again.');
      }
    }
  };

  // Calculate Pro Analytics
  const getAnalytics = () => {
    if (!stats || !applications.length) {
      return { responseRate: 0, applied: 0, interview: 0, offer: 0 };
    }
    
    const applied = stats.by_status?.Applied || 0;
    const interview = stats.by_status?.Interview || 0;
    const offer = stats.by_status?.Offer || 0;
    const total = stats.total_applications || 1;
    
    const responseRate = total > 0 ? Math.round((interview / total) * 100) : 0;
    
    return { responseRate, applied, interview, offer, total };
  };

  // Upgrade Modal Component
  const UpgradeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⭐</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Pro Features</h3>
          <p className="text-gray-600">
            Get access to advanced analytics, CSV export, email reminders, and unlimited job tracking.
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6">
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Response rate analytics
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Export to CSV
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Email follow-up reminders
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Unlimited job tracking
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              setShowUpgradeModal(false);
              navigate('/pricing');
            }}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-colors"
          >
            Upgrade to Pro
          </button>
          <button
            onClick={() => setShowUpgradeModal(false)}
            className="w-full py-3 px-6 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );

  // Pro Insights Dashboard Component
  const ProInsightsDashboard = () => {
    const analytics = getAnalytics();
    
    return (
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">Pro Insights</h3>
            {!isPro && (
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                PRO
              </span>
            )}
          </div>
          <button
            onClick={handleExportCSV}
            disabled={exportLoading}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              isPro 
                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' 
                : 'bg-gray-100 text-gray-500 cursor-pointer'
            }`}
          >
            {exportLoading ? (
              <svg className="animate-spin w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            Download CSV
            {!isPro && <span className="ml-1">🔒</span>}
          </button>
        </div>

        {isPro ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Response Rate Widget */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
              <p className="text-sm text-blue-600 font-medium mb-1">Response Rate</p>
              <p className="text-4xl font-bold text-blue-900">{analytics.responseRate}%</p>
              <p className="text-xs text-blue-600 mt-1">
                {analytics.interview} interviews from {analytics.total} applications
              </p>
            </div>

            {/* Funnel Widget */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
              <p className="text-sm text-purple-600 font-medium mb-3">Application Funnel</p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-xs text-gray-600 w-16">Applied</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 mx-2">
                    <div 
                      className="bg-blue-500 h-3 rounded-full transition-all"
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold w-8">{analytics.applied}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-gray-600 w-16">Interview</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 mx-2">
                    <div 
                      className="bg-yellow-500 h-3 rounded-full transition-all"
                      style={{ width: analytics.applied > 0 ? `${(analytics.interview / analytics.applied) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold w-8">{analytics.interview}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-gray-600 w-16">Offer</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 mx-2">
                    <div 
                      className="bg-green-500 h-3 rounded-full transition-all"
                      style={{ width: analytics.applied > 0 ? `${(analytics.offer / analytics.applied) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold w-8">{analytics.offer}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="bg-gray-50 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setShowUpgradeModal(true)}
          >
            <div className="flex items-center gap-3">
              <div className="text-2xl">📊</div>
              <p className="text-gray-600 text-sm">Unlock analytics to see your response rate and application funnel</p>
            </div>
            <span className="inline-flex items-center text-purple-600 font-medium text-sm whitespace-nowrap">
              Upgrade to Pro <span className="ml-1">→</span>
            </span>
          </div>
        )}
      </div>
    );
  };

  // Limit Modal Component
  const LimitModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Limit Reached</h3>
          <p className="text-gray-600">
            You have <strong>20 active applications</strong>—the maximum for the Free plan.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-700 mb-3">
            <strong>To add a new application:</strong>
          </p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start">
              <span className="mr-2">📁</span>
              Archive old applications you&apos;re no longer pursuing
            </li>
            <li className="flex items-start">
              <span className="mr-2">❌</span>
              Mark applications as Rejected or Withdrawn
            </li>
            <li className="flex items-start">
              <span className="mr-2">⭐</span>
              Or upgrade to Pro for unlimited tracking
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              setShowLimitModal(false);
              navigate('/pricing');
            }}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-colors"
          >
            Upgrade to Pro
          </button>
          <button
            onClick={() => setShowLimitModal(false)}
            className="w-full py-3 px-6 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
          >
            I&apos;ll Archive Some Jobs
          </button>
        </div>
      </div>
    </div>
  );

  // Fit Score Modal Component
  // ========================================
  // CLEAN FIT SCORE MODAL (Refactored)
  // ========================================
  const FitScoreModal = () => {
    const [resumeText, setResumeText] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [fitScoreResult, setFitScoreResult] = useState(null);
    
    const handleCalculate = async () => {
      setFitScoreLoading(true);
      try {
        const jobText = selectedAppForFitScore.job_description;
        
        console.log('[FitScore] Starting calculation...');
        console.log('[FitScore] Job text length:', jobText?.length);
        console.log('[FitScore] Resume text length:', resumeText?.length);
        
        // Call the clean simple match API
        const result = await freeToolsAPI.simpleMatch(jobText, resumeText);
        
        console.log('[FitScore] Raw result:', result);
        console.log('[FitScore] match_score:', result.match_score);
        console.log('[FitScore] missing_skills:', result.missing_skills);
        console.log('[FitScore] summary:', result.summary);
        
        // Store result and clear resume text (Zero-Retention)
        setFitScoreResult({
          match_score: result.match_score,
          missing_skills: result.missing_skills || [],
          summary: result.summary || ''
        });
        setResumeText(''); // Clear resume data immediately
        setShowResults(true);
        
        // Update the application with the fit score
        await jobTrackerAPI.updateApplication(selectedAppForFitScore.id, {
          fit_score: result.match_score,
          fit_score_updated: new Date().toISOString()
        });
        
        await loadData();
        console.log('[FitScore] Success! Score saved.');
        
        await loadData();
        console.log('[FitScore] Success! Score saved.');
        
        // Trigger credit update
        window.dispatchEvent(new Event('credit_update'));
        
      } catch (err) {
        console.error('[FitScore] Error:', err);
        console.error('[FitScore] Response:', err.response?.data);
        
        // Handle 402 Payment Required
        if (err.response?.status === 402) {
          setShowFitScoreModal(false);
          setShowPaymentModal(true);
          return;
        }
        
        setError('Failed to calculate fit score. Please try again.');
        setShowFitScoreModal(false);
      } finally {
        setFitScoreLoading(false);
      }
    };

    const handleClose = () => {
      setShowFitScoreModal(false);
      setSelectedAppForFitScore(null);
      setResumeText('');
      setShowResults(false);
      setFitScoreResult(null);
    };

    const getScoreColor = (score) => {
      if (score >= 70) return 'text-green-600';
      if (score >= 50) return 'text-amber-600';
      return 'text-red-600';
    };

    const getScoreBg = (score) => {
      if (score >= 70) return 'bg-green-50 border-green-200';
      if (score >= 50) return 'bg-amber-50 border-amber-200';
      return 'bg-red-50 border-red-200';
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {showResults ? 'Your Fit Score' : `Calculate Fit Score for ${selectedAppForFitScore?.role}`}
            </h3>
            <button 
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {showResults && fitScoreResult ? (
            // Results View - Resume data has been cleared
            <div>
              <div className={`${getScoreBg(fitScoreResult.match_score)} rounded-xl p-6 mb-6 border text-center`}>
                <p className="text-sm text-gray-600 mb-2">Your Match Score</p>
                <p className={`text-6xl font-bold ${getScoreColor(fitScoreResult.match_score)}`}>
                  {fitScoreResult.match_score}%
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {fitScoreResult.match_score >= 70 ? 'Strong match!' : fitScoreResult.match_score >= 50 ? 'Good potential' : 'Needs optimization'}
                </p>
              </div>

              {/* Summary */}
              {fitScoreResult.summary && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                  <p className="text-gray-600 text-sm">{fitScoreResult.summary}</p>
                </div>
              )}

              {/* Missing Skills */}
              {fitScoreResult.missing_skills && fitScoreResult.missing_skills.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Missing Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {fitScoreResult.missing_skills.map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm border border-red-200">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Cleared Notice */}
              <div className="flex items-center justify-center text-sm text-green-600 bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Your resume data has been permanently cleared from memory.
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            // Input View
            <>
              <p className="text-sm text-gray-600 mb-4">
                Paste your resume to see how well you match this role at <strong>{selectedAppForFitScore?.company}</strong>.
              </p>

              <textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste your resume content here..."
                rows={10}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />

              {/* Privacy Assurance - Zero Retention Policy */}
              <div className="flex items-start mt-3 text-sm text-gray-500">
                <span className="mr-2 text-lg">🔒</span>
                <p>
                  <strong>Privacy First:</strong> We analyze in-memory and delete immediately. We do not save a copy to your cloud. Please keep your original file safe.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCalculate}
                  disabled={fitScoreLoading || resumeText.length < 50}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {fitScoreLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    'Calculate Fit Score'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const LimitsDisplay = () => {
    if (!limits) return null;

    const activePercentage = limits.max_active_applications > 0 
      ? Math.round((limits.active_applications_count / limits.max_active_applications) * 100)
      : 0;

    const newPercentage = limits.max_new_applications_30d > 0
      ? Math.round((limits.new_applications_last_30d / limits.max_new_applications_30d) * 100) 
      : 0;

    if (limits.plan === 'pro') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-2 animate-pulse"></div>
            <span className="font-semibold text-purple-800">Pro Plan</span>
          </div>
          <p className="text-sm text-purple-600 mt-2">Unlimited applications, reminders & analytics</p>
        </div>
      );
    }

    // Calculate warning states
    const isActiveNearLimit = activePercentage >= 75;
    const isActiveAtLimit = activePercentage >= 100;
    const isNewNearLimit = newPercentage >= 75;

    return (
      <div className="space-y-4">
        {/* Main Limits Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-blue-900">Free Plan</span>
            <Link to="/pricing" className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200 transition-colors">
              Upgrade
            </Link>
          </div>
          
          {/* Active Applications */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">Active Applications</span>
                <div className="relative ml-1 group">
                  <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-52 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    <strong>Active = Applied, Interview, Offer, or On Hold.</strong>
                    <br/>Archived, Rejected &amp; Withdrawn don&apos;t count!
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
              <span className={`text-sm font-bold ${isActiveAtLimit ? 'text-red-600' : isActiveNearLimit ? 'text-amber-600' : 'text-blue-700'}`}>
                {limits.active_applications_count}/{limits.max_active_applications}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  isActiveAtLimit ? 'bg-red-500' : isActiveNearLimit ? 'bg-amber-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(activePercentage, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* New Applications */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">New This Month</span>
                <div className="relative ml-1 group">
                  <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Rolling 30-day limit. Resets as older applications age out.
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
              <span className={`text-sm font-bold ${isNewNearLimit ? 'text-amber-600' : 'text-green-600'}`}>
                {limits.new_applications_last_30d}/{limits.max_new_applications_30d}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  isNewNearLimit ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(newPercentage, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Helpful Tip */}
          <div className="bg-white/60 rounded-md p-2.5 mt-3">
            <p className="text-xs text-blue-700 flex items-start">
              <span className="mr-1.5">💡</span>
              <span>
                <strong>Tip:</strong> Archive or reject old applications to free up space!
              </span>
            </p>
          </div>
        </div>

        {/* Warning Banner - At Limit */}
        {!limits.can_add_new && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-xl mr-3">⚠️</span>
              <div>
                <p className="text-sm font-semibold text-amber-900 mb-1">You&apos;ve hit your limit</p>
                <p className="text-xs text-amber-700 mb-2">
                  {isActiveAtLimit 
                    ? `You have ${limits.max_active_applications} active applications. Archive or reject some to add more.`
                    : `You've added ${limits.max_new_applications_30d} applications this month.`
                  }
                </p>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline underline-offset-2"
                >
                  Go Pro for unlimited tracking, reminders &amp; analytics →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Near Limit Warning */}
        {limits.can_add_new && (isActiveNearLimit || isNewNearLimit) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-700 flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Approaching limit. Consider archiving old applications or upgrading.
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your job applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="sticky top-0 z-40 bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="AlignifyNOW" className="h-[50px] sm:h-[80px] w-auto" />
              <span className="text-base sm:text-lg font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            
            {/* Desktop Navigation - Simplified */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Tool Hub
              </Link>
              <Link to="/tracker" className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-1">
                Job Tracker
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Pricing
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link-desktop"
                  className="text-sm font-medium text-purple-700 hover:text-purple-900 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Admin
                </Link>
              )}
              
              {/* Credit Counter */}
              <CreditCounter />
              
              {/* Account Dropdown */}
              {user && (
                <div className="flex items-center gap-2 ml-2 pl-4 border-l border-gray-200">
                    {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-gray-200" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                            {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </div>
                    )}
                    <span className="text-sm font-medium text-gray-700">
                        {user.name?.split(' ')[0] || 'Account'}
                    </span>
                    <button
                        onClick={logout}
                        className="ml-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
                        title="Sign out"
                    >
                        Sign Out
                    </button>
                </div>
              )}
            </div>
            
            {/* Mobile Menu Button & Credits */}
            <div className="flex md:hidden items-center gap-3">
              <CreditCounter />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t mt-2 pt-4 pb-4 space-y-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-900 bg-gray-50 rounded-lg">
                🏠 Tool Hub
              </Link>
              <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                💰 Pricing
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link-mobile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-3 text-base font-medium text-purple-700 hover:bg-purple-50 rounded-lg"
                >
                  🛡️ Admin
                </Link>
              )}
              {user && (
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center gap-3 px-3 py-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                        {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{user.name || user.email}</p>
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="text-sm text-red-600"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Job Tracker</h1>
              <p className="text-sm sm:text-base text-gray-600">Track and manage your job applications</p>
            </div>
            <button
              onClick={() => {
                if (limits && !limits.can_add_new) {
                  setShowLimitModal(true);
                } else {
                  setShowCreateForm(true);
                }
              }}
              className="inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-base sm:text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98]"
            >
              <svg className="w-5 h-5 sm:w-4 sm:h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Application
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Success Message Display */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 text-sm">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Pro Insights Dashboard */}
        <ProInsightsDashboard />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Limits Display */}
            <LimitsDisplay />

            {/* Status Filter */}
            <div className="mt-6 bg-white p-4 rounded-xl shadow-sm border">
              <h3 className="font-semibold text-gray-900 mb-3">Filter by Status</h3>
              <div className="space-y-2">
                {statuses.map(status => (
                  <button
                    key={status.value}
                    onClick={() => setSelectedStatus(status.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedStatus === status.value 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{status.label}</span>
                      {stats && stats.by_status[status.value] !== undefined && status.value !== 'all' && (
                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                          {stats.by_status[status.value]}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats */}
            {stats && (
              <div className="mt-6 bg-white p-4 rounded-xl shadow-sm border">
                <h3 className="font-semibold text-gray-900 mb-3">Statistics</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Applications:</span>
                    <span className="font-semibold">{stats.total_applications}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Applications:</span>
                    <span className="font-semibold">{stats.active_applications}</span>
                  </div>
                </div>
                
                {stats.upgrade_suggestions.length > 0 && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                    {stats.upgrade_suggestions.map((suggestion, index) => (
                      <p key={index} className="text-blue-700">{suggestion}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {applications.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border p-8 text-center">
                <div className="max-w-md mx-auto">
                  {/* Welcome Icon */}
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  
                  {/* Welcome Message */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Track your first job application.
                  </h2>
                  <p className="text-gray-600 mb-8">
                    Your private dashboard is ready. Connect to Google Drive to save your data.
                  </p>
                  
                  {/* Add Job Button */}
                  <button
                    onClick={() => {
                      if (limits && !limits.can_add_new) {
                        setShowLimitModal(true);
                      } else {
                        setShowCreateForm(true);
                      }
                    }}
                    className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow-lg shadow-purple-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Job Manually
                  </button>

                  {/* Quick Tip */}
                  <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Quick tip:</strong> Use the Match Score tool on the home page to analyze a job, then click &quot;Save to Tracker&quot; to add it here automatically.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map(app => {
                  const fitScore = getFitScoreColor(app.fit_score);
                  const isActive = activeStatuses.includes(app.status);
                  
                  return (
                    <div key={app.id} className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-grow">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {app.role} at {app.company}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${getStatusColor(app.status)}-100 text-${getStatusColor(app.status)}-800`}>
                              {app.status}
                            </span>
                            {/* Fit Score Badge */}
                            <button
                              onClick={() => handleOpenFitScore(app)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${fitScore.bg} ${fitScore.text} hover:opacity-80 transition-opacity cursor-pointer`}
                              title={app.fit_score ? `Fit Score: ${app.fit_score}%` : 'Click to calculate Fit Score'}
                            >
                              {app.fit_score ? `🎯 ${fitScore.label}` : '🎯 Check Fit'}
                            </button>
                            {app.is_duplicate_merged && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Merged
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                            {app.location && (
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {app.location}
                              </div>
                            )}
                            {app.salary_range && (
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                                {app.salary_range}
                              </div>
                            )}
                            {app.applied_date && (
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Applied: {formatDate(app.applied_date)}
                              </div>
                            )}
                            {app.source && (
                              <div className="flex items-center" data-testid={`app-source-${app.id}`}>
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                via {app.source}
                              </div>
                            )}
                          </div>

                          {app.job_url && (
                            <div className="mb-3">
                              <a 
                                href={app.job_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm underline"
                              >
                                View Job Posting →
                              </a>
                            </div>
                          )}

                          {app.notes && (
                            <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                              {app.notes}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          {/* Reminder Bell - Pro Feature */}
                          <div className="relative">
                            <button
                              onClick={() => {
                                if (!isPro) {
                                  setShowUpgradeModal(true);
                                } else {
                                  setShowReminderPopover(showReminderPopover === app.id ? null : app.id);
                                }
                              }}
                              className={`p-1 rounded transition-colors ${
                                app.follow_up_date 
                                  ? 'text-yellow-500 hover:text-yellow-600' 
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={app.follow_up_date ? `Reminder: ${formatDate(app.follow_up_date)}` : 'Set follow-up reminder'}
                            >
                              <svg className="w-4 h-4" fill={app.follow_up_date ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                              {!isPro && <span className="absolute -top-1 -right-1 text-xs">🔒</span>}
                            </button>
                            
                            {/* Reminder Popover */}
                            {showReminderPopover === app.id && (
                              <div className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border p-4 w-64">
                                <p className="text-sm font-medium text-gray-900 mb-2">Set Follow-up Reminder</p>
                                <input
                                  type="date"
                                  value={reminderDate}
                                  onChange={(e) => setReminderDate(e.target.value)}
                                  min={new Date().toISOString().split('T')[0]}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleSetReminder(app.id)}
                                    className="flex-1 bg-blue-600 text-white text-sm py-1.5 rounded-lg hover:bg-blue-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowReminderPopover(null);
                                      setReminderDate('');
                                    }}
                                    className="flex-1 bg-gray-100 text-gray-700 text-sm py-1.5 rounded-lg hover:bg-gray-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                {app.follow_up_date && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    Current: {formatDate(app.follow_up_date)}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditApplication(app)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit application"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          
                          <select
                            value={app.status}
                            onChange={(e) => handleUpdateStatus(app.id, e.target.value)}
                            className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                          >
                            {statuses.filter(s => s.value !== 'all').map(status => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                          
                          {/* Archive Button - Only show for active applications */}
                          {isActive && (
                            <button
                              onClick={() => handleArchiveApplication(app.id)}
                              className="text-gray-500 hover:text-gray-700 p-1"
                              title="Archive application"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteApplication(app.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete application"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 border-t pt-2">
                        Created: {formatDate(app.created_at)} • Updated: {formatDate(app.updated_at)}
                        {app.fit_score_updated && (
                          <span> • Fit Score: {formatDate(app.fit_score_updated)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <AddJobModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={handleCreateApplication}
        statuses={statuses}
      />
      <EditJobModal
        isOpen={showEditForm}
        onClose={() => {
          setShowEditForm(false);
          setSelectedAppForEdit(null);
        }}
        onSubmit={handleSaveEditedApplication}
        application={selectedAppForEdit}
        statuses={statuses}
      />
      {showLimitModal && <LimitModal />}
      {showFitScoreModal && <FitScoreModal />}
      {showUpgradeModal && <UpgradeModal />}
      <PaymentModal 
        isOpen={showPaymentModal} 
        onClose={() => setShowPaymentModal(false)} 
      />
    </div>
  );
};

export default JobTracker;
